import {
  DRAG_N_DROP_HANDLE_ATTR_NAME,
  CLONED_ELEMENT_ID,
  DETECT_INTERSECTION_THROTTLE_TIMEOUT_MS,
  SCROLL_THRESHOLD_PX,
  SCROLL_SPEED_PX,
  CHECK_IS_SCROLLING_NEEDED_THROTTLE_TIMEOUT_MS,
  SCROLL_INTERVAL_MS,
} from "./constants";
import { CoordinatesXY, DragNDropIntersectionQuarter, ElemWithSizeAndCoord, DnDItemData } from "@composables/useDragNDrop/types";
import { Ref, TemplateRef } from "vue";
import findNearestParent from "@logic/dom/FindNearestParent";
import lodashThrottle from "lodash-es/throttle";

// ============================================================================
export function createGhost(nodeToClone: HTMLElement, ghostOpacity: string): Element {
  const targetSize = nodeToClone.getBoundingClientRect();

  const ghostNode: HTMLElement = (nodeToClone as HTMLElement).cloneNode(true);
  ghostNode.style.cursor = "grabbing";

  const dragAndDropHandle = (ghostNode as HTMLElement).querySelector(`*[${DRAG_N_DROP_HANDLE_ATTR_NAME}]`) as HTMLElement;
  if (dragAndDropHandle) {
    dragAndDropHandle.style.setProperty("cursor", "grabbing", "important");
  }

  ghostNode.id = CLONED_ELEMENT_ID;

  ghostNode.style.setProperty("width", `${targetSize.width}px`, "important");
  ghostNode.style.setProperty("min-width", `${targetSize.width}px`, "important");
  ghostNode.style.setProperty("max-width", `${targetSize.width}px`, "important");

  ghostNode.style.setProperty("height", `${targetSize.height}px`, "important");
  ghostNode.style.setProperty("min-height", `${targetSize.height}px`, "important");
  ghostNode.style.setProperty("max-height", `${targetSize.height}px`, "important");

  ghostNode.style.setProperty("position", "fixed", "important");
  ghostNode.style.setProperty("top", "0", "important");
  ghostNode.style.setProperty("left", "0", "important");
  ghostNode.style.setProperty("z-index", "99999", "important");

  ghostNode.style.setProperty("opacity", ghostOpacity, "important");

  return document.body.appendChild(ghostNode);
}

// ============================================================================
export function defineIntersectionQuarter(cursorCoord: CoordinatesXY, elemWithSizeAndCoord: ElemWithSizeAndCoord): DragNDropIntersectionQuarter {
  const { x: elemX, y: elemY, width: elemWidth, height: elemHeight } = elemWithSizeAndCoord;

  // Calcs element center
  const elemCenterX = elemX + elemWidth / 2;
  const elemCenterY = elemY + elemHeight / 2;

  if (cursorCoord.x < elemCenterX) return cursorCoord.y < elemCenterY ? 1 : 4;
  else return cursorCoord.y < elemCenterY ? 2 : 3;
}

// ============================================================================
export function findElemToDrag(eventTarget: Element, elemsInvolvedInDnD: Ref<Array<TemplateRef>>, commonParent: Element): Element | null {
  const allHandlesArray = Array.from(commonParent.querySelectorAll(`*[${DRAG_N_DROP_HANDLE_ATTR_NAME}]`));

  // Checks if eventTarget is a handle
  let closestHandle: Element | null = allHandlesArray.find(h => h === eventTarget);

  // If eventTarget is not a handle itself, looks through its parents and check if one of them is a handle
  // commonParent is included in the parentsArray to stop the search when it reaches the commonParent node
  if (!closestHandle) {
    closestHandle = findNearestParent(eventTarget, [...allHandlesArray, commonParent]);
  }

  // Returns if the event.target is not a handle nor any of its parents a handle
  if (closestHandle === commonParent) {
    return;
  }

  // Finds the nearest element from the elemsInvolvedInDnD
  const elementToDrag = findNearestParent(closestHandle, elemsInvolvedInDnD.value);

  return elementToDrag || null;
}

// ============================================================================
// TODO: Ensure that the TemplateRef has only one root DOM node (throw an error or warning in the mouseDown/touchDown handler)
export function getElemWithSizeAndCoordList(elemsInvolvedInDnD: Ref<Array<TemplateRef>>): Array<ElemWithSizeAndCoord> {
  return elemsInvolvedInDnD.value.map((element: TemplateRef) => {
    const { width, height, left, top } = element.getBoundingClientRect();
    return { domNode: element, width, height, x: left, y: top };
  });
}

// ============================================================================
export function setGhostSamePositionAsCursor(element: HTMLElement, cursorCoord: CoordinatesXY, ghostShift: CoordinatesXY) {
  if (!element) return;

  element.style.setProperty("left", cursorCoord.x - ghostShift.x + "px", "important");
  element.style.setProperty("top", cursorCoord.y - ghostShift.y + "px", "important");
}

// ============================================================================
export const detectIntersectionsThrottled = lodashThrottle(detectIntersections, DETECT_INTERSECTION_THROTTLE_TIMEOUT_MS);

function detectIntersections(elemWithSizeAndCoordList: Array<ElemWithSizeAndCoord>, cursorCoord: CoordinatesXY): Array<DnDItemData> {
  let dndItemDataArray: DnDItemData = [];

  const allIntersectedElems = elemWithSizeAndCoordList.filter(({ width, height, x, y }) => {
    const isIntersectHorizontally = cursorCoord.x >= x && cursorCoord.x <= x + width;
    const isIntersectVertically = cursorCoord.y >= y && cursorCoord.y <= y + height;

    return isIntersectHorizontally && isIntersectVertically;
  });

  dndItemDataArray = allIntersectedElems.map(elemWithSizeAndCoord => {
    const intersectionQuarter = defineIntersectionQuarter(cursorCoord, elemWithSizeAndCoord);

    return {
      domNode: elemWithSizeAndCoord.domNode,
      dataset: elemWithSizeAndCoord.domNode.dataset,
      intersectionQuarter,
    } as DnDItemData;
  });

  return dndItemDataArray;
}

// ============================================================================
// Scrolls the page when the user moves "grab" cursor
// close to the top or bottom viewport edge
let scrollInterval = null;

// trailing is set false to avoid function execution after the cursor left the activation area
export const checkIsScrollingNeededThrottled = lodashThrottle(checkIsScrollingNeeded, CHECK_IS_SCROLLING_NEEDED_THROTTLE_TIMEOUT_MS, { leading: true, trailing: false });

export function checkIsScrollingNeeded(cursorCoord: CoordinatesXY): boolean {
  const windowHeight = window.innerHeight;

  if (cursorCoord.y < SCROLL_THRESHOLD_PX) {
    startScrolling("up");
    return true;
  } else if (cursorCoord.y > windowHeight - SCROLL_THRESHOLD_PX) {
    startScrolling("down");
    return true;
  } else {
    // Cursor is not near the edge. Stops scrolling
    if (scrollInterval) {
      clearInterval(scrollInterval);
      scrollInterval = null;
    }
    return false;
  }
}

function startScrolling(direction) {
  if (scrollInterval) return; // scrolling is going on

  scrollInterval = setInterval(() => {
    if (direction === "up") {
      window.scrollBy({
        top: -SCROLL_SPEED_PX,
        left: 0,
      });
    } else {
      window.scrollBy({
        top: SCROLL_SPEED_PX,
        left: 0,
      });
    }
  }, SCROLL_INTERVAL_MS);
}
